import React, { memo, useCallback, useRef, useState } from 'react';
import { Col, HasAccess, Row, Typography } from 'components';
import styles from 'pages/admin/dashboard/dashboard.module.scss';
import { TabNav } from 'pages/admin/dashboard/tabNav';
import { Outlet, useLocation } from 'react-router-dom';
import { DashboardTasks } from 'pages/admin/dashboard/Tasks';
import { PERMISSIONS } from '../../../constants/permissions';

const DashboardEntityTypes = {
  ALL: { label: 'All' },
  PURCHASE_REQUEST: { label: 'Purchase Request', permission: PERMISSIONS.purchaseRequests.purchaseRequest.read },
  DEAL: { label: 'Deals', permission: PERMISSIONS.deals.deal.read },
  LEAD: { label: 'Leads', permission: PERMISSIONS.leads.lead.read },
  TASK: { label: 'Tasks', permission: PERMISSIONS.tasks.task.read },
};

type EntityType = keyof typeof DashboardEntityTypes;
type EntityCounts = Record<EntityType, number>;

export const Dashboard = memo(() => {
  const [selectedEntity, setSelectedEntity] = useState<EntityType>('ALL');
  const [entityCounts, setEntityCounts] = useState<EntityCounts>({
    ALL: 0,
    PURCHASE_REQUEST: 0,
    DEAL: 0,
    LEAD: 0,
    TASK: 0,
  });

  const { pathname } = useLocation();
  // Keep track of previous counts to prevent unnecessary updates
  const prevCountsRef = useRef<Omit<EntityCounts, 'ALL'>>({
    PURCHASE_REQUEST: 0,
    DEAL: 0,
    LEAD: 0,
    TASK: 0,
  });

  const handleFilterClick = useCallback(<T extends string>(entity: T) => {
    setSelectedEntity(entity as EntityType);
  }, []);

  // Update entity count when a child component reports its total
  const updateEntityCount = useCallback((entity: string, count: number) => {
    // Skip 'ALL' as it's derived from the other counts
    if (entity === 'ALL') return;

    // Only update if the count has actually changed
    if (entity in prevCountsRef.current && prevCountsRef.current[entity as Exclude<EntityType, 'ALL'>] !== count) {
      prevCountsRef.current[entity as Exclude<EntityType, 'ALL'>] = count;

      setEntityCounts((prev) => {
        const newCounts = {
          ...prev,
          [entity]: count,
        };
        // Recalculate total
        const total = newCounts.PURCHASE_REQUEST + newCounts.DEAL + newCounts.LEAD + newCounts.TASK;
        return {
          ...newCounts,
          ALL: total,
        };
      });
    }
  }, []);

  return (
    <Col span={24} className={styles.main_dashboard}>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Typography.Title className={styles.pageTitle} level={3} title={'Dashboard'}>
            Dashboard
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Row gutter={[12, 12]} wrap={false}>
            <Col flex={1}>
              <Row gutter={[0, 16]}>
                <Col span={24} style={{ paddingLeft: '32px' }}>
                  <TabNav />
                </Col>
                <Col span={24} style={{ paddingLeft: '32px' }}>
                  <Outlet
                    context={{
                      selectedEntity,
                      entityTypes: DashboardEntityTypes,
                      entityCounts,
                      onFilterClick: handleFilterClick,
                      onUpdateCount: updateEntityCount,
                    }}
                  />
                </Col>
              </Row>
            </Col>
            {pathname === '/admin/dashboard/list' && (
              <Col span={8} className={'dashboard-tasks'}>
                <HasAccess role={PERMISSIONS.tasks.task.read}>
                  <DashboardTasks />
                </HasAccess>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Col>
  );
});
